<script setup>
  /* global defineEmits, defineProps */
  import { computed } from 'vue'
  import { formatGenerationDuration, replayOutputLabel, replayTtsSpeedAdjustment } from './replayLabels'

  const props = defineProps({
    outputs: { type: Array, default: () => [] },
    taskStatus: { type: String, default: '' },
    activeKey: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    allowRegenerate: Boolean,
    busy: Boolean,
  })
  const emit = defineEmits(['select', 'regenerate'])
  const playable = (output) => output.status === 'COMPLETED' && Boolean(output.videoUrl)
  const progress = (output) => Math.max(0, Math.min(99, Math.round(Number(output.progress) || 0)))
  const groups = computed(() => {
    const isTranslation = (output) =>
      output.audioType === 'TTS' || output.outputKind === 'SUBTITLE_DUB' || String(output.id || '').startsWith('tts:')
    const isBaseline = (output) => output.id === 'baseline' || output.outputKind === 'MEETING_MATERIALS'
    return [
      {
        key: 'meeting',
        label: '会议版本',
        outputs: props.outputs
          .filter((output) => !isTranslation(output))
          .sort((a, b) => Number(isBaseline(b)) - Number(isBaseline(a))),
      },
      { key: 'translations', label: '翻译配音', outputs: props.outputs.filter(isTranslation) },
    ].filter((group) => group.outputs.length)
  })
  const regenerating = (output) => ['QUEUED', 'PROCESSING', 'RETRY_WAIT'].includes(output.regenerationStatus)
  const status = (output) => {
    if (output.regenerationStatus === 'PROCESSING')
      return `重新生成中 ${Math.max(0, Math.min(99, output.regenerationProgress || 0))}%`
    if (output.regenerationStatus === 'QUEUED') return '重新生成已排队'
    if (output.regenerationStatus === 'RETRY_WAIT') return '重新生成等待重试'
    if (playable(output)) return !props.readonly && output.key === props.activeKey ? '当前播放' : '可播放'
    if (output.status === 'PROCESSING') return `生成中 ${progress(output)}%`
    if (['QUEUED', 'RETRY_WAIT'].includes(props.taskStatus) && output.status === 'FAILED') return '等待重试'
    if (output.status === 'FAILED') return '生成失败'
    if (['BLOCKED', 'COMPLETED', 'COMPLETED_WITH_WARNINGS'].includes(props.taskStatus)) return '未生成'
    return '等待生成'
  }
</script>

<template>
  <div v-if="outputs.length" aria-label="成片语言版本" class="output-groups">
    <section v-for="group in groups" :key="group.key" :aria-label="group.label" :class="['output-group', group.key]">
      <header class="group-heading">
        <h3>{{ group.label }}</h3>
        <span>{{ group.outputs.filter(playable).length }} / {{ group.outputs.length }} 可播放</span>
      </header>
      <div class="language-switcher">
        <div v-for="output in group.outputs" :key="output.key || output.id" class="output-card">
          <button
            :aria-pressed="!readonly && output.key === activeKey"
            :class="[
              'language-option',
              {
                active: !readonly && output.key === activeKey,
                processing: output.status === 'PROCESSING' || regenerating(output),
              },
            ]"
            :disabled="readonly || !playable(output) || output.key === activeKey"
            :title="output.errorMessage || ''"
            type="button"
            @click="emit('select', output)"
          >
            <span class="output-title">{{ replayOutputLabel(output) }}</span>
            <small class="output-status">{{ status(output) }}</small>
            <progress
              v-if="output.status === 'PROCESSING' || output.regenerationStatus === 'PROCESSING'"
              :aria-label="`${replayOutputLabel(output)}生成进度`"
              max="100"
              :value="
                output.regenerationStatus === 'PROCESSING'
                  ? Math.max(0, Math.min(99, output.regenerationProgress || 0))
                  : progress(output)
              "
            />
            <small v-if="playable(output)">
              视频时长 {{ formatGenerationDuration(output.videoDuration == null ? null : output.videoDuration * 1000) }}
            </small>
            <small v-if="output.generationDurationMs != null || playable(output)">
              {{ output.status === 'PROCESSING' ? '已耗时' : '生成耗时' }}
              {{ formatGenerationDuration(output.generationDurationMs) }}
            </small>
            <small
              v-if="replayTtsSpeedAdjustment(output)"
              class="speed-notice"
              :title="replayTtsSpeedAdjustment(output).detail"
            >
              局部配音加速
            </small>
          </button>
          <button
            v-if="allowRegenerate && output.id !== 'baseline'"
            class="regenerate-button"
            :disabled="busy || !output.canRegenerate"
            type="button"
            @click="emit('regenerate', output)"
          >
            {{ regenerating(output) ? '正在制作' : output.status === 'FAILED' ? '重试该语种' : '重新生成该语种' }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
  .output-groups {
    display: grid;
    margin-top: 20px;
    gap: 22px;
  }

  .group-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 10px;
    gap: 12px;

    h3 {
      margin: 0;
      color: #303133;
      font-size: 14px;
      font-weight: 600;
    }

    span {
      color: #909399;
      font-size: 12px;
    }
  }

  .language-switcher {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
    gap: 10px;
  }

  .meeting .language-switcher {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .output-card {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 6px;
  }
  .regenerate-button {
    align-self: flex-end;
    padding: 4px 0;
    border: 0;
    background: none;
    color: #1677c8;
    cursor: pointer;
    font-size: 12px;
  }
  .regenerate-button:disabled {
    color: #909399;
    cursor: default;
  }
  .language-option {
    width: 100%;
    flex: 1;
    display: flex;
    min-width: 0;
    padding: 12px 14px;
    color: #606266;
    background: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    cursor: pointer;
    flex-direction: column;
    text-align: left;
    transition: 0.2s ease;
    gap: 5px;

    small {
      color: #909399;
    }

    .output-title {
      color: #303133;
      line-height: 1.5;
    }

    .speed-notice {
      align-self: flex-start;
      margin-top: 3px;
      padding: 3px 6px;
      color: #8a5d12;
      background: #fff4db;
      border-radius: 4px;
      font-size: 11px;
    }

    &.active .output-title,
    &.active .output-status {
      color: var(--el-color-primary, #1890ff);
    }

    &.active,
    &:not(:disabled):hover {
      color: var(--el-color-primary, #1890ff);
      background: #ecf5ff;
      border-color: var(--el-color-primary, #1890ff);
      transform: translateY(-1px);
    }

    &.processing {
      background: #f0f7ff;
      border-color: #a0cfff;

      .output-status {
        color: #1677c8;
      }
    }

    &:disabled {
      cursor: default;
    }

    progress {
      width: 100%;
      height: 6px;
      overflow: hidden;
      border: 0;
      border-radius: 3px;
      background: #dce9f7;
      accent-color: #1890ff;

      &::-webkit-progress-bar {
        background: #dce9f7;
      }

      &::-webkit-progress-value {
        background: #1890ff;
      }

      &::-moz-progress-bar {
        background: #1890ff;
      }
    }
  }
  @media (max-width: 600px) {
    .meeting .language-switcher {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
